import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowUpRight, Download } from 'lucide-react';
import { Button } from './ui/button';
import { openInquiryPopup } from '../lib/contact';

export default function Footer() {
  return (
    <footer id="contact" className="relative bg-blue-hero text-white pt-16 pb-6 overflow-hidden">
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55zm-40 90c-15 4-30 15-30 30 0 5 3 8 8 8h124c5 0 8-3 8-8 0-15-15-26-30-30-8-2-18-4-32-4-14 0-24 2-32 4-6 1-11 0-16 0z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />
      {/* Dark scrim for readability */}
      <div className="absolute inset-0 bg-black/15 pointer-events-none" />
      {/* Top red accent bar */}
      <div className="absolute top-0 inset-x-0 h-1 bg-brand" />
      <div className="absolute top-1 inset-x-0 h-[1px] bg-white/40" />

      <div className="relative container mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* BRAND COLUMN */}
          <div>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white ring-4 ring-brand/40 shadow-xl shadow-brand/20 flex items-center justify-center overflow-hidden shrink-0">
                <img src="/logo.jpg" alt="Russian Education" className="w-full h-full object-cover" />
              </div>
              <div className="leading-[1.05]">
                <div className="font-serif text-2xl font-extrabold text-white tracking-tight drop-shadow-sm">Russian</div>
                <div className="font-serif text-2xl font-extrabold tracking-tight text-brand drop-shadow-sm">Education</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/90 leading-relaxed font-medium">
              Your premium, ISO-certified gateway for direct MBBS admissions to NMC-approved Russian government medical universities. Guiding medical careers across India.
            </p>

            <a
              href="/russian-education-brochure.pdf"
              download="Russian-Education-MBBS-Brochure.pdf"
              data-testid="download-brochure-btn"
              className="mt-6 group inline-flex items-center gap-3 w-full sm:w-auto bg-white hover:bg-brand text-primary hover:text-white font-bold rounded-xl px-4 py-3 shadow-lg shadow-black/20 transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-brand text-white group-hover:bg-white group-hover:text-brand flex items-center justify-center transition-colors shrink-0">
                <Download className="w-4 h-4" strokeWidth={2.4} />
              </div>
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-widest font-bold text-brand group-hover:text-white transition-colors">Free PDF</div>
                <div className="text-sm font-serif font-extrabold">Download Brochure</div>
              </div>
            </a>
            <div className="mt-6 flex gap-2.5">
              {[
                { Ic: Facebook, label: 'Facebook' },
                { Ic: Twitter, label: 'Twitter' },
                { Ic: Instagram, label: 'Instagram' },
                { Ic: Linkedin, label: 'LinkedIn' },
                { Ic: Youtube, label: 'YouTube' },
              ].map(({ Ic, label }, i) => (
                <a key={i} href="#" aria-label={label}
                  className="w-10 h-10 rounded-full bg-white/15 hover:bg-brand border border-white/30 hover:border-brand flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-brand/40 group backdrop-blur-sm">
                  <Ic className="w-4 h-4 text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* QUICK LINKS */}
          <div>
            <h4 className="font-serif text-xl font-extrabold text-white uppercase tracking-wide">Quick Links</h4>
            <div className="mt-2 h-1 w-16 bg-brand rounded-full shadow-sm shadow-brand/30" />
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                { label: 'Services', href: '/services' },
                { label: 'About Us', href: '/about' },
                { label: 'Universities', href: '/universities' },
                { label: 'Admission Process', href: '/admission-process' },
                { label: 'NMC Regulations', href: '/nmc-regulations' },
                { label: 'Register Now', href: '/seminar' },
              ].map((item) => (
                <li key={item.label}>
                  {item.href.startsWith('/') ? (
                    <Link to={item.href}
                      className="text-white/95 hover:text-white font-medium flex items-center gap-2.5 group transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand group-hover:scale-150 group-hover:shadow-sm group-hover:shadow-brand/60 transition-all" />
                      <span className="border-b border-white/0 hover:border-white/60 pb-0.5">{item.label}</span>
                    </Link>
                  ) : (
                    <a href={item.href}
                      className="text-white/95 hover:text-white font-medium flex items-center gap-2.5 group transition-colors">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand group-hover:scale-150 group-hover:shadow-sm group-hover:shadow-brand/60 transition-all" />
                      <span className="border-b border-white/0 hover:border-white/60 pb-0.5">{item.label}</span>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* OUR SERVICES */}
          <div>
            <h4 className="font-serif text-xl font-extrabold text-white uppercase tracking-wide">Our Services</h4>
            <div className="mt-2 h-1 w-16 bg-brand rounded-full shadow-sm shadow-brand/30" />
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                { label: 'Direct Enrollment', href: '/services' },
                { label: '1-on-1 Guidance', href: '/video-counselling' },
                { label: 'Eligibility Analysis', href: '/services' },
                { label: 'NMC Rule Counseling', href: '/exams' },
                { label: 'Visa Support', href: '/visa-support' },
                { label: 'Accommodation', href: '/accommodation' },
              ].map((item) => (
                <li key={item.label}>
                  <Link to={item.href}
                    className="text-white/95 hover:text-white font-medium flex items-center gap-2.5 group transition-colors">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand group-hover:scale-150 group-hover:shadow-sm group-hover:shadow-brand/60 transition-all" />
                    <span className="border-b border-white/0 hover:border-white/60 pb-0.5">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT INFO */}
          <div>
            <h4 className="font-serif text-xl font-extrabold text-white uppercase tracking-wide">Contact Info</h4>
            <div className="mt-2 h-1 w-16 bg-brand rounded-full shadow-sm shadow-brand/30" />
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3" data-testid="footer-office-head">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Head Office</div>
                  <span className="text-white/95 leading-relaxed font-medium">
                    Alex Hospital, Shiv Gate,<br />Sector 115, Noida
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-office-corporate">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Corporate Office</div>
                  <span className="text-white/95 leading-relaxed font-medium">
                    God Gift Complex, Hiran Magri<br />Sector 3, Udaipur
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Phone</div>
                  <a href="tel:+919509406802" data-testid="footer-phone" className="text-white font-bold hover:text-brand text-base transition-colors">+91 95094 06802</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Email</div>
                  <a href="mailto:info@russianeducation.in" className="text-white font-medium hover:text-brand break-all transition-colors">info@russianeducation.in</a>
                </div>
              </li>
            </ul>
            <Button onClick={openInquiryPopup} data-testid="footer-book-consult" className="mt-6 bg-brand hover:bg-brand-dark text-white rounded-full h-12 px-6 w-full font-bold shadow-lg shadow-brand/40 group border border-white/20">
              Book Free Consultation
              <ArrowUpRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-14 pt-6 border-t border-white/25 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/90 font-medium">
          <div>© {new Date().getFullYear()} Russian Education. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white hover:underline">Privacy Policy</a>
            <a href="#" className="hover:text-white hover:underline">Terms of Service</a>
            <a href="#" className="hover:text-white hover:underline">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
